'use client'

import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
} from '@dc-shared-web/components/shadcn/form'
import { Input } from '@dc-shared-web/components/shadcn/input'
import { Button } from '@dc-shared-web/components/shadcn/button'
import { BaseDialog, useAuth, useCreateProject } from '@/dc-shared-web/src'
import { SELECTED_TEAM_ID_KEY } from '@dc-shared-web/lib/constants'
import { usePathname, useSearchParams } from 'next/navigation'
import { useTemporaryHighlight } from '@/app/core/hooks/useTemporaryHighlight'

const formSchema = z.object({
  name: z.string().min(1),
})

export function CreateProjectDialog({
  onDialogClose,
  folderId,
  folderUuid,
}: {
  onDialogClose: () => void
  folderId?: string
  folderUuid?: string
}) {
  const params = useSearchParams()
  const selectedTeamId = params.get(SELECTED_TEAM_ID_KEY)
  const { user } = useAuth()
  const team = user.teams.find((t) => t.id === selectedTeamId)
  const { mutate: createProject, isLoading: isCreatingProject } =
    useCreateProject()
  const form = useForm<z.infer<typeof formSchema>>({
    defaultValues: {
      name: '',
    },
    resolver: zodResolver(formSchema),
  })
  const pathname = usePathname()
  const addTempHighlight = useTemporaryHighlight()

  const onCreateSubmit = async (data: z.infer<typeof formSchema>) => {
    if (!team?.teamId) {
      throw new Error('Team ID is required')
    }
    return createProject(
      {
        name: data.name,
        folderUuid,
        office_uuid: selectedTeamId || '',
      },
      {
        onSuccess: (data) => {
          if (data.uuid) {
            addTempHighlight('newProjectId', data.uuid, pathname)
          }
          onDialogClose()
        },
      }
    )
  }

  return (
    <BaseDialog
      title='New project'
      isOpen
      onClose={onDialogClose}
      primaryActionSlot={
        <Button
          disabled={!form.formState.isValid || isCreatingProject}
          type='submit'
          data-testid='create-project-confirm-btn'
          onClick={form.handleSubmit(onCreateSubmit)}
        >
          {isCreatingProject ? 'Creating...' : 'Create'}
        </Button>
      }
    >
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onCreateSubmit)}>
          <FormField
            control={form.control}
            name='name'
            render={({ field }) => (
              <FormItem>
                <FormLabel className='tw-sr-only'>Project name</FormLabel>
                <FormControl>
                  <Input
                    placeholder='Enter project name'
                    {...field}
                    autoFocus
                  />
                </FormControl>
              </FormItem>
            )}
          />
        </form>
      </Form>
    </BaseDialog>
  )
}
